/* NUO Announcement Bar — rotating messages, countdown, manual navigation. */
(() => {
  'use strict';

  if (customElements.get('nuo-announcement-bar')) return;

  const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

  const parseTarget = (str) => {
    const value = (str || '').trim();
    const hasExplicitTimezone = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?(?:Z|[+-]\d{2}:\d{2})$/.test(value);

    if (hasExplicitTimezone) {
      const timestamp = Date.parse(value);
      return Number.isNaN(timestamp) ? null : new Date(timestamp);
    }

    const m = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/.exec(value);
    if (!m) return null;
    return new Date(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0));
  };

  const pad = (n) => String(n).padStart(2, '0');

  class NuoAnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.nuo-ab__item'));
      this.index = Math.max(0, this.items.findIndex((item) => !item.hidden));
      this.mode = this.dataset.mode || 'both';
      this.interval = (parseFloat(this.dataset.interval) || 5) * 1000;
      this.transition = this.dataset.transition || 'slide';
      this.duration = parseInt(this.dataset.duration, 10) || 400;
      this.animating = false;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.duration = 0;
      }

      this.countdowns = [];
      this.querySelectorAll('[data-nuo-countdown]').forEach((el) => {
        const target = parseTarget(el.dataset.nuoCountdown);
        if (target) this.countdowns.push({ el, target, item: el.closest('.nuo-ab__item') });
      });

      if (this.countdowns.length) {
        this._tickCountdowns();
        this._cdTimer = setInterval(() => this._tickCountdowns(), 1000);
      }

      this.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-nuo-ab-nav]');
        if (!btn) return;
        this._stop();
        this._go(btn.dataset.nuoAbNav === 'next' ? 1 : -1);
        this._start();
      });

      let touchX = null;
      this.addEventListener('touchstart', (event) => { touchX = event.touches[0].clientX; }, { passive: true });
      this.addEventListener('touchend', (event) => {
        if (touchX === null) return;
        const dx = event.changedTouches[0].clientX - touchX;
        touchX = null;
        if (Math.abs(dx) > 40 && this._visibleItems().length > 1) {
          this._stop();
          this._go(dx < 0 ? 1 : -1);
          this._start();
        }
      }, { passive: true });

      if (this.hasAttribute('data-pause-on-hover')) {
        this.addEventListener('mouseenter', () => this._stop());
        this.addEventListener('mouseleave', () => this._start());
      }

      this._onVisibility = () => (document.hidden ? this._stop() : this._start());
      document.addEventListener('visibilitychange', this._onVisibility);

      if (window.Shopify && window.Shopify.designMode) {
        this._sectionEl = this.closest('.shopify-section');
        if (this._sectionEl) {
          this._sectionEl.addEventListener('shopify:block:select', (event) => {
            const item = event.target.closest ? event.target.closest('.nuo-ab__item') : null;
            const i = this.items.indexOf(item);
            if (i >= 0) {
              this._stop();
              this._show(i);
            }
          });
          this._sectionEl.addEventListener('shopify:block:deselect', () => this._start());
        }
      }

      this._refresh();
      this._start();
    }

    disconnectedCallback() {
      this._stop();
      clearInterval(this._cdTimer);
      document.removeEventListener('visibilitychange', this._onVisibility);
    }

    _visibleItems() {
      return this.items.filter((item) => item.dataset.nuoExpiredHidden !== 'true');
    }

    _start() {
      this._stop();
      if (this.mode === 'nav') return;
      if (this._visibleItems().length < 2) return;
      this._timer = setInterval(() => this._go(1), this.interval);
    }

    _stop() {
      clearInterval(this._timer);
      this._timer = null;
    }

    _show(index) {
      this.items.forEach((item, i) => {
        item.hidden = i !== index;
        item.classList.toggle('is-active', i === index);
      });
      this.index = index;
    }

    _nextIndex(direction) {
      const visible = this._visibleItems();
      if (visible.length === 0) return -1;
      const current = this.items[this.index];
      let pos = visible.indexOf(current);
      if (pos === -1) pos = 0;
      const nextPos = (pos + direction + visible.length) % visible.length;
      return this.items.indexOf(visible[nextPos]);
    }

    _go(direction) {
      if (this.animating) return;
      const nextIndex = this._nextIndex(direction);
      if (nextIndex === -1 || nextIndex === this.index) return;

      const current = this.items[this.index];
      const next = this.items[nextIndex];
      this.index = nextIndex;

      if (this.duration === 0) {
        current.hidden = true;
        current.classList.remove('is-active');
        next.hidden = false;
        next.classList.add('is-active');
        return;
      }

      let outFrames;
      let inFrames;

      if (this.transition === 'fade') {
        outFrames = [{ opacity: 1 }, { opacity: 0 }];
        inFrames = [{ opacity: 0 }, { opacity: 1 }];
      } else if (this.transition === 'slide-up') {
        outFrames = [
          { transform: 'translateY(0)', opacity: 1 },
          { transform: `translateY(${direction * -100}%)`, opacity: 0 },
        ];
        inFrames = [
          { transform: `translateY(${direction * 100}%)`, opacity: 0 },
          { transform: 'translateY(0)', opacity: 1 },
        ];
      } else {
        outFrames = [
          { transform: 'translateX(0)', opacity: 1 },
          { transform: `translateX(${direction * -100}%)`, opacity: 0 },
        ];
        inFrames = [
          { transform: `translateX(${direction * 100}%)`, opacity: 0 },
          { transform: 'translateX(0)', opacity: 1 },
        ];
      }

      this.animating = true;

      next.hidden = false;
      next.classList.add('is-active');

      const options = { duration: this.duration, easing: EASING, fill: 'both' };
      const outAnim = current.animate(outFrames, options);
      next.animate(inFrames, options);

      outAnim.onfinish = () => {
        current.hidden = true;
        current.classList.remove('is-active');
        this.animating = false;
      };
      outAnim.oncancel = () => {
        this.animating = false;
      };
    }

    _tickCountdowns() {
      const now = Date.now();
      let changed = false;

      this.countdowns = this.countdowns.filter(({ el, target, item }) => {
        const diff = target.getTime() - now;

        if (diff <= 0) {
          if (item && item.dataset.nuoHideExpired === 'true') {
            item.dataset.nuoExpiredHidden = 'true';
            changed = true;
          } else {
            el.textContent = '00:00:00';
          }
          return false;
        }

        const days = Math.floor(diff / 86400000);
        const hours = Math.floor((diff % 86400000) / 3600000);
        const minutes = Math.floor((diff % 3600000) / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        el.textContent = days > 0
          ? `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
          : `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
        return true;
      });

      if (!this.countdowns.length) {
        clearInterval(this._cdTimer);
        this._cdTimer = null;
      }

      if (changed) this._refresh();
    }

    _refresh() {
      const visible = this._visibleItems();
      const section = this.closest('.shopify-section');

      if (visible.length === 0) {
        this._stop();
        (section || this).style.display = 'none';
        document.documentElement.style.setProperty('--announcement-bar-height', '0px');
        return;
      }

      const current = this.items[this.index];
      if (current.dataset.nuoExpiredHidden === 'true') {
        this._show(this.items.indexOf(visible[0]));
      }

      if (visible.length < 2) {
        this._stop();
        this.querySelectorAll('.nuo-ab__arrow').forEach((btn) => { btn.hidden = true; });
      }
    }
  }

  customElements.define('nuo-announcement-bar', NuoAnnouncementBar);
})();
